import React, {useCallback, useEffect, useMemo, useState, useContext} from 'react';
import PropTypes from 'prop-types';
import {Box, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination , TableRow, TableSortLabel, styled, tableCellClasses, Paper, Backdrop, Typography, IconButton, 
    CircularProgress, TextField, InputAdornment, Button, Stack,
    Dialog,
    MenuItem} from '@mui/material';
import { Edit, DeleteForever, Search, Upload, Download } from '@mui/icons-material';
import Header from './Header';
import {CompteForm, EditCompteForm} from './compte-form';
import { API } from "../conf";
import { Context } from "../App";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
}));
const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
}));

function descendingComparator(a, b) {
  if (b < a) {
    return -1;
  }
  if (b > a) {
    return 1;
  }
  return 0;
}

function getComparator(order) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b)
    : (a, b) => -descendingComparator(a, b);
}

function stableSort(array, table, orderBy, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = orderBy >-1 ? comparator(table[orderBy]?.sortBy(a[0]), table[orderBy]?.sortBy(b[0])) : 0;
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function EnhancedTableHead(props) {
    const { order, orderBy, onRequestSort, table } =    props;
    
    const createSortHandler = (property) => (event) => onRequestSort(event, property)

    return (
        <TableHead>
        <StyledTableRow>
            {table.map((col, index) => (
            <StyledTableCell
                key={index}
                align='center'
                sx={{fontWeight:'bold'}}
                sortDirection={orderBy === index ? order : false}
            >
                    {col.sortable? <TableSortLabel
                    active={orderBy === index}
                    direction={orderBy === index ? order : 'asc'}
                    onClick={createSortHandler(index)}
                    >
                        {col.header}
                    </TableSortLabel> : col.header}
            </StyledTableCell>
            ))}
        </StyledTableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.number.isRequired,
};

export default function CompteManager() {
    const { updateConnect } = useContext(Context)
    const [organisation, setOrganisation] = useState(JSON.parse(sessionStorage.getItem('eglise')))
    const [agent, setAgent] = useState(JSON.parse(sessionStorage.getItem('user')))
    const [eglises, setEglises] = useState([])
    const [egliseId, setEgliseId] = useState('')
    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState(1);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);

    const [loading, setLoad] = useState(true)
    const [importing, setImport] = useState(false)
    const [msg, setMsg] = useState('')
    const [open, setOpen] = useState(false)
    const [action, setAction] = useState('')
    const [search_input, setSearch] = useState('')
    const [comptes, setComptes] = useState([])
    const [compte, setCompte] = useState()

    const table = useCallback(()=>[
        {header: "n° compte", sortable: true, sortBy:(el)=>el.num_compte.toLowerCase(), row: (row, index)=><StyledTableCell key={index} align="center" sx={{minWidth:'100px'}}>{row.num_compte}</StyledTableCell>},
        {header: "Libellé", sortable: true, sortBy:(el)=>el.libelle.toLowerCase(), row: (row, index)=><StyledTableCell key={index} align="center" sx={{minWidth:'150px', textTransform:'capitalize'}}>{row.libelle}</StyledTableCell>},
        {header: "Type", sortable: true, sortBy:(el)=>el.type+(el.is_departement?'département': el.is_offrande?'offrande':''), row: (row, index)=><StyledTableCell key={index} align="center" sx={{minWidth:'150px', textTransform:'capitalize'}}>
            {row.type+(row.is_departement?'/département': row.is_offrande?'/offrande':'')}
        </StyledTableCell>},
        {header: " % offrande globale", sortable: true, sortBy:(el)=>el.percent, row: (row, index)=><StyledTableCell key={index} align="center" sx={{minWidth:'70px'}}>{row.percent.toFixed(2)}</StyledTableCell>},
        {header: "Budget prévisionel (€)", sortable: true, sortBy:(el)=>el.prevision, row: (row, index)=><StyledTableCell key={index} align="center" sx={{minWidth:'70px'}}>{row.prevision>0?row.prevision.toFixed(2)+'€' : ''}</StyledTableCell>},
        {header: "Écart (€)", sortable: true, sortBy:(el)=>el.ecart, row: (row, index)=><StyledTableCell key={index} align="center" sx={{minWidth:'70px'}}>{row.prevision>0&&row.ecart?row.ecart.toFixed(2)+'€' : ''}</StyledTableCell>},
        {header: "Solde (€)", sortable: true, sortBy:(el)=>el.solde, row: (row, index)=><StyledTableCell key={index} align="center" sx={{minWidth:'70px'}}>{row.solde.toFixed(2)}€</StyledTableCell>},
        {header: "", sortable: false, row: (row, index)=><StyledTableCell key={index} align='left'>
            <IconButton aria-label="Edit" onClick={()=>{setCompte(row); manage_dialog(true, 'edit')}}><Edit fontSize="small"/></IconButton>
            <IconButton aria-label="delete" disabled={agent.level<3&&['liaison', 'petite-caisse',].includes(row.type)} onClick={()=>{setCompte(row); manage_dialog(true, 'remove')}}><DeleteForever color={agent.level<3&&['liaison', 'petite-caisse',].includes(row.type)?'grey':'carmine'} fontSize="small"/></IconButton>
        </StyledTableCell>}
    ], [])

    const eglise = useMemo(()=>organisation&&!organisation.isFederation ? organisation : eglises.find(el=>el._id===egliseId), [egliseId, eglises, organisation])

    function manage_dialog(status, action) {
        setOpen(status)
        setAction(action)
        if (!status) {
            setCompte()
        }
    }

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    function normalize(str) {
        return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9\s-.,_+]/gi,'').toLowerCase()
    }

    function get_searchable(item) {
        return item.num_compte+' '+item.libelle+' '+(item.type==='petite-caisse'?'petite caisse': item.is_departement?'département': item.is_offrande?'offrande':'')+' '+item.percent.toFixed(2)+' '+item.solde.toFixed(2)+(item.prevision>0?' '+item.prevision.toFixed(2):'')+(item.prevision>0&&item.ecart?' '+item.ecart.toFixed(2):'')
    }

    function handleDelete(){
        manage_dialog(false, ''); setLoad(true)
        fetch(API.concat(`/comptes/${compte.num}`), {
            method:'DELETE',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + sessionStorage.getItem('token')
            }
        }).then(async response=>{
            const token = response.headers.get('x-auth-token')
            if(response.status === 401) {
                sessionStorage.clear()
                updateConnect(false)
            }else {
                if (token && token !== sessionStorage.getItem('token') && token !== '') {
                    sessionStorage.setItem('token', response.headers.get('x-auth-token'))
                }
                if (response.status === 200) {
                    setComptes(old=> old.filter(cpt=> cpt._id !== compte._id))
                    setCompte()
                }
                setLoad(false)
            }
        })
    }

    function updateComptes(newCompte){
        if (!comptes.find(cpt=> cpt===newCompte)) {
            setComptes(old=> [...old, newCompte])
        }
    }

    function updateCompte(newCompte){
        setComptes(old=> old.map(cpt=>cpt._id===newCompte._id? newCompte: cpt))
    }

    useEffect(()=>{
        if (eglise&&!eglise.isFederation&&comptes.reduce((tt,cpt)=>tt+cpt.percent,0)<100) {
            setMsg('Attention! Répartition du budget incomplète.')
        } else if(eglise&&!eglise.isFederation&&comptes.filter(cpt=>!['petite-caisse', 'liaison'].includes(cpt.type)).reduce((tt,cpt)=>tt+cpt.solde,0)!==comptes.find(cpt=>cpt.type==='liaison').solde) {
            setMsg('Attention! Solde compte principal différent du solde total des comptes.')
        } setMsg('')
    }, [comptes, eglise])

    useEffect(()=>{
        if(eglise) {
            setLoad(true)
            fetch(API.concat(`/comptes/eglises/${eglise.number}`), {
                method:'GET',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + sessionStorage.getItem('token')
                }
            }).then(async response=>{
                const token = response.headers.get('x-auth-token')
                if(response.status === 401) {
                    sessionStorage.clear()
                    updateConnect(false)
                }else {
                    if (token && token !== sessionStorage.getItem('token') && token !== '') {
                        sessionStorage.setItem('token', response.headers.get('x-auth-token'))
                    }
                    if (response.status === 200) {
                        const json = await response.json()
                        if (!eglise.isFederation&&json.reduce((tt,cpt)=>tt+cpt.percent,0)<100) {
                            setMsg('Attention! Répartition du budget incomplète.')
                        } else setMsg('')
                        setComptes(json)
                    }
                    setLoad(false)
                }
            })
        }
    }, [updateConnect, eglise])

    useEffect(()=>{
        if(organisation?.isFederation) {
            setLoad(true)
            fetch(API.concat(`/eglises/by/${JSON.stringify(agent.eglises?.length>0 ? {_id:{$in: agent.eglises}}:agent.level>=4?{isUnion: false}:{isFederation: false, isUnion: false})}`), {
                method:'GET',
                headers:{
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + sessionStorage.getItem('token')
                }
            }).then(async(response) => {
                const token = response.headers.get('x-auth-token')
                if(response.status === 401) {
                    sessionStorage.clear()
                    updateConnect(false)
                }else {
                    if (token && token !== sessionStorage.getItem('token') && token !== '') {
                        sessionStorage.setItem('token', response.headers.get('x-auth-token'))
                    }
                    if (response.status === 200) {
                        const json = await response.json()
                        setEglises(json)
                        setLoad(false)
                    }
                }
            })
        }
    }, [organisation, agent])

    useEffect(()=>{
        setOrganisation(JSON.parse(sessionStorage.getItem('eglise')))
        setAgent(JSON.parse(sessionStorage.getItem('user')))
    },[]) 

    const visibleRows = useMemo(() => stableSort(comptes.filter(cpt=> normalize(get_searchable(cpt)).includes(normalize(search_input))), table(), orderBy, getComparator(order)).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [comptes, table, order, orderBy, page, rowsPerPage, search_input]);

    return (
        <div style={{display:'flex', flexDirection:'column', paddingBottom:'30px'}}>
            <Header/>
            <Typography sx={{textAlign:'center', marginTop:'20px', fontSize:26}}>Gestion des Comptes</Typography>
            {organisation?.isFederation&&<TextField select size="small" label="église" sx={{alignSelf:'end',marginBottom:1, width:'25%', marginRight:3, minWidth:'200px'}} value={egliseId} onChange={e=>setEgliseId(old=>{if(old!==e.target.value)setComptes([]); return e.target.value})}>
                {eglises.map(eglise=><MenuItem key={eglise._id} value={eglise._id}>{`${eglise.nom} - ${eglise.commune.toUpperCase()}`}</MenuItem>)}
            </TextField>}
            <Box display='flex' flexDirection='column' sx={{paddingX:'20px', marginTop:'30px'}}>
                {msg!==''&&<Typography variant='subtitle1' color='carmine.main' align='center' marginBottom={2}>{msg}</Typography>}
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBlock:'10px'}}>
                    <TextField size="small" label="Rechercher un compte par n°, libellé, type, %, solde" disabled={!comptes?.length>0} sx={{width:'60%', minWidth:'300px', alignSelf:'center'}}
                    value={search_input} onChange={(event)=> setSearch(event.target.value)}
                    InputProps={{endAdornment: <InputAdornment position="end"><Search /></InputAdornment>}}/>
                    
                    <Stack direction='row' spacing={1}>
                        {/* <Button variant='contained' startIcon={<Download />} onClick={()=>setImport(true)}>Importer</Button> */}
                        <Button variant='contained' onClick={()=>manage_dialog(true, 'add')}>Nouveau</Button>
                    </Stack>
                </div>

                <Box sx={{ width: '100%', marginTop:'15px' }}>
                    <Paper sx={{ width: '100%', overflow:'hidden' }}>
                        <TableContainer>
                            <Table
                                sx={{ minWidth: 700 }}
                                aria-labelledby="tableTitle"
                            >
                                <EnhancedTableHead
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={handleRequestSort}
                                table={table()}
                                />
                                <TableBody>
                                {visibleRows.map((row, index) =>
                                    <StyledTableRow key={index} >
                                        {table().map((col, index)=> col.row(row, index))}
                                    </StyledTableRow>
                                )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        labelRowsPerPage='lignes par page'
                        labelDisplayedRows={({ from, to, count, page }) => { return `${from}–${to} sur ${count !== -1 ? count : `plus de ${to}`}`; }}
                        component="div"
                        count={comptes.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(event, npage)=> setPage(npage)}
                        onRowsPerPageChange={(event)=> {setRowsPerPage(parseInt(event.target.value, 10)); setPage(0);}}
                        />
                    </Paper>
                </Box>
            </Box>
            <Backdrop open={loading} ><CircularProgress color="inherit" /></Backdrop>
            <Dialog open={open}>
                {action==='remove'&&<Paper sx={{minWidth:'250px', width:'40%', paddingX:'15px', paddingY:'25px'}}>
                    <Typography variant='h5'>Supprimer un compte</Typography>
                    <Typography variant='body1'>Voulez-vous supprimer le compte n°{compte.num_compte} <span style={{textTransform:'capitalize'}}>{compte.libelle}</span> de la liste de vos comptes ?</Typography>
                    <Stack direction='row' spacing={1} alignItems='center' justifyContent='flex-end'>
                        <Button variant='outlined' color='carmine' onClick={()=>manage_dialog(false, '')}>Annuler</Button>
                        <Button variant='contained' color='carmine' onClick={handleDelete}>Supprimer</Button>
                    </Stack>
                </Paper>}
                {action==='add'&&<CompteForm add={true} eglise={eglise} updateComptes={updateComptes} close={()=> manage_dialog(false, '')}/>}
                {action==='edit'&&<EditCompteForm edit={true} compte={compte} updateCompte={updateCompte} close={()=>manage_dialog(false, '')} />} 
            </Dialog>            
        </div>
    );
}
